// ── FriendRating.js ───────────────────────────────────────────────────────
// Public page — friend received a unique link: beelove.me/#rate/TOKEN
//
// Two-pass questionnaire (all 10 axes shown at once per pass):
//   Pass 'left'  — "Comment est [Prénom] ?" — 10-row table
//   Pass 'trans' — transition screen
//   Pass 'right' — "L'être idéal pour [Prénom]" — 10-row table
//   Step 2       — Optional portrait phrase + submit
//   Step 3       — Done + conversion CTA

import { useState, useEffect } from 'react';
import { getEffectiveTranslations } from '../utils/i18n';

function t(lang, key) {
  return getEffectiveTranslations(lang || 'en')[key] || key;
}

// ── Portrait phrases — 10 curated, neutral, positive ─────────────────────
const PORTRAIT_PHRASES = {
  fr: [
    "Une personne qui fait du bien autour d'elle.",
    "Quelqu'un de fiable et authentique.",
    "Une personnalité chaleureuse et ouverte.",
    "Une personne qui sait ce qu'elle veut.",
    "Quelqu'un qui met les autres à l'aise naturellement.",
    "Une présence positive difficile à ignorer.",
    "Quelqu'un sur qui on peut vraiment compter.",
    "Une personne curieuse et pleine de vie.",
    "Quelqu'un qui donne sans compter.",
    "Une énergie rare, sincère et entière.",
  ],
  en: [
    "Someone who makes the people around them feel good.",
    "Reliable and genuinely themselves.",
    "A warm, open personality.",
    "Someone who knows what they want.",
    "Puts people at ease naturally.",
    "A positive presence that's hard to ignore.",
    "Someone you can really count on.",
    "Curious and full of life.",
    "Gives without keeping score.",
    "A rare energy — sincere and whole.",
  ],
};

// ── 10 axes: 5 relational (Layer B) + 5 identity (Layer A) ───────────────
function getAxes(lang, firstName) {
  const n  = firstName || 'them';
  const fr = lang === 'fr';
  return [
    // ── Layer B — Relational ──────────────────────────────────────────────
    {
      key:'repair', layer:'B',
      label: t(lang,'axis.repair') || 'Repair',
      question: fr
        ? `Quand les choses se compliquent entre ${n} et quelqu'un de proche…`
        : `When things go wrong between ${n} and someone close…`,
      leftLabel:  fr ? `${n} a tendance à…`          : `${n} tends to…`,
      rightLabel: fr ? 'Leur partenaire idéal devrait…' : 'Their ideal partner would…',
      statements: {
        left: fr ? [
          "Éviter le sujet et passer à autre chose",
          "Attendre que ça se tasse avant d'en parler",
          "Aborder le problème quand iel se sent prêt·e",
          "Affronter la situation assez rapidement",
          "Toujours régler les problèmes directement et sans attendre",
        ] : [
          "Avoid the issue and move on",
          "Wait for things to cool down before talking",
          "Bring it up when they feel ready",
          "Address issues fairly quickly and directly",
          "Always confront things head-on without delay",
        ],
        right: fr ? [
          "Laisser les choses se tasser naturellement",
          "Éviter d'insister quand ce n'est pas urgent",
          "Aborder les tensions au bon moment",
          "Parler des problèmes dès qu'ils se posent",
          "Toujours confronter les conflits immédiatement",
        ] : [
          "Let things settle naturally on their own",
          "Not push to talk unless it's really needed",
          "Address tension when the moment feels right",
          "Bring up issues as soon as they arise",
          "Always address conflict head-on right away",
        ],
      },
    },
    {
      key:'conflict', layer:'B',
      label: t(lang,'axis.conflict') || 'Conflict',
      question: fr
        ? `Comment ${n} gère-t-il·elle la tension ?`
        : `How does ${n} handle tension?`,
      leftLabel:  fr ? `${n} est…`                  : `${n} is…`,
      rightLabel: fr ? "Iel s'entend mieux avec…"    : 'They pair best with…',
      statements: {
        left: fr ? [
          "Très peu à l'aise avec le conflit, cherche la paix avant tout",
          "Évite généralement la confrontation",
          "S'exprime quand c'est vraiment important",
          "Direct·e et à l'aise avec le désaccord",
          "Très affirmatif·ve, exprime ses besoins clairement à chaque fois",
        ] : [
          "Very conflict-averse, keeps the peace at all costs",
          "Usually steps back to avoid confrontation",
          "Speaks up when it really matters",
          "Direct and comfortable with disagreement",
          "Very assertive, expresses needs clearly every time",
        ],
        right: fr ? [
          "Quelqu'un de très calme qui n'aime pas les tensions",
          "Quelqu'un de patient qui évite la confrontation",
          "Quelqu'un capable de s'exprimer au bon moment",
          "Quelqu'un de direct·e et honnête",
          "Quelqu'un d'aussi affirmatif·ve et direct·e",
        ] : [
          "Someone very calm who dislikes tension",
          "Someone patient who avoids confrontation",
          "Someone who can speak up at the right moment",
          "Someone direct and honest",
          "Someone equally assertive and direct",
        ],
      },
    },
    {
      key:'accountability', layer:'B',
      label: t(lang,'axis.accountability') || 'Accountability',
      question: fr
        ? `Quand ${n} fait une erreur…`
        : `When ${n} makes a mistake…`,
      leftLabel:  fr ? `${n} a tendance à…`          : `${n} tends to…`,
      rightLabel: fr ? 'Leur partenaire idéal devrait…' : 'Their ideal partner would…',
      statements: {
        left: fr ? [
          "Dévier la responsabilité ou blâmer les autres",
          "Minimiser un peu sa part de responsabilité",
          "Admettre ses torts quand on le lui signale",
          "Prendre ses responsabilités sans qu'on ait besoin de demander",
          "Assumer pleinement et immédiatement chaque erreur",
        ] : [
          "Deflect responsibility or blame others",
          "Minimize their role somewhat",
          "Acknowledge mistakes when pointed out",
          "Take responsibility without being pushed",
          "Fully own every mistake immediately",
        ],
        right: fr ? [
          "Ne jamais tenir rancune et tourner la page facilement",
          "Pardonner facilement et sans condition",
          "Apprécier les excuses mais savoir passer à autre chose",
          "Valoriser la responsabilité et l'honnêteté",
          "Avoir les mêmes standards élevés d'imputabilité",
        ] : [
          "Never hold grudges and move on easily",
          "Forgive easily and unconditionally",
          "Appreciate apologies but know how to move on",
          "Value accountability and honesty",
          "Hold the same high standards for accountability",
        ],
      },
    },
    {
      key:'generosity', layer:'B',
      label: t(lang,'axis.generosity') || 'Generosity',
      question: fr
        ? `Dans ses amitiés et ses relations, ${n}…`
        : `In friendships and relationships, ${n}…`,
      leftLabel:  fr ? `${n} est…`                  : `${n} is…`,
      rightLabel: fr ? "Iel s'épanouit avec…"         : 'They flourish with…',
      statements: {
        left: fr ? [
          "Principalement centré·e sur ses propres besoins",
          "Donne quand c'est pratique pour lui·elle",
          "Généreux·se de manière significative avec les proches",
          "Vraiment attentif·ve et généreux·se en temps et en énergie",
          "L'une des personnes les plus généreuses que je connaisse",
        ] : [
          "Mostly focused on their own needs",
          "Gives when it's convenient for them",
          "Meaningfully generous with those they're close to",
          "Genuinely attentive and giving with time and energy",
          "One of the most generous people I know",
        ],
        right: fr ? [
          "Quelqu'un d'indépendant qui n'a pas besoin de beaucoup d'attention",
          "Quelqu'un qui respecte l'espace personnel",
          "Quelqu'un de chaleureux mais également autonome",
          "Quelqu'un d'aussi attentif·ve et présent·e",
          "Quelqu'un d'également généreux·se et dévoué·e",
        ] : [
          "Someone independent who doesn't need much attention",
          "Someone who respects personal space",
          "Someone warm but also self-sufficient",
          "Someone equally attentive and present",
          "Someone just as generous and devoted",
        ],
      },
    },
    {
      key:'presence', layer:'B',
      label: t(lang,'axis.presence') || 'Presence',
      question: fr
        ? `Quand tu passes du temps avec ${n}, iel est…`
        : `When you spend time with ${n}, they are…`,
      leftLabel:  fr ? `${n} est…`                  : `${n} is…`,
      rightLabel: fr ? "Iel a besoin de quelqu'un qui…" : 'They need someone who…',
      statements: {
        left: fr ? [
          "Souvent distrait·e, rarement totalement là",
          "Présent·e quand ça l'intéresse, ailleurs sinon",
          "Généralement attentif·ve en conversation",
          "Vraiment présent·e, te fait sentir écouté·e",
          "Totalement là — téléphone rangé, attention pleine",
        ] : [
          "Often distracted, rarely fully present",
          "Present when interested, drifts otherwise",
          "Generally attentive in conversation",
          "Truly present, makes you feel heard",
          "Completely there — phone away, full attention",
        ],
        right: fr ? [
          "Donne de l'espace et n'a pas besoin de toute l'attention",
          "Est à l'aise avec une présence variable",
          "Peut s'adapter selon les moments",
          "Est également présent·e et attentif·ve",
          "Est totalement présent·e, 100% du temps",
        ] : [
          "Gives space and doesn't need full attention",
          "Is comfortable with variable presence",
          "Can adapt depending on the moment",
          "Is equally present and attentive",
          "Is fully present, 100% of the time",
        ],
      },
    },
    // ── Layer A — Identity ────────────────────────────────────────────────
    {
      key:'narrative', layer:'A',
      label: t(lang,'axis.narrative') || 'Self-awareness',
      question: fr
        ? `Est-ce que ${n} sait qui iel est et où iel va ?`
        : `Does ${n} know who they are and where they're going?`,
      leftLabel:  fr ? `${n} semble…`               : `${n} seems…`,
      rightLabel: fr ? 'Iel se connecterait bien avec…' : "They'd connect well with…",
      statements: {
        left: fr ? [
          "Encore en train de se chercher, sans direction claire",
          "A quelques repères mais reste incertain·e",
          "Se connaît assez bien",
          "Clair·e sur son identité et sa direction",
          "Exceptionnellement ancré·e — sait exactement qui iel est",
        ] : [
          "Still figuring themselves out, no clear direction",
          "Has some bearings but still uncertain",
          "Knows themselves fairly well",
          "Clear on their identity and direction",
          "Exceptionally grounded — knows exactly who they are",
        ],
        right: fr ? [
          "Quelqu'un qui explore encore sa propre identité",
          "Quelqu'un d'ouvert à la découverte de soi",
          "Quelqu'un de stable mais encore en croissance",
          "Quelqu'un d'également ancré et conscient de lui-même",
          "Quelqu'un d'aussi sûr·e de lui·elle et accompli·e",
        ] : [
          "Someone still exploring their own identity",
          "Someone open to self-discovery",
          "Someone stable but still growing",
          "Someone equally grounded and self-aware",
          "Someone just as sure of themselves and accomplished",
        ],
      },
    },
    {
      key:'stability', layer:'A',
      label: t(lang,'axis.stability') || 'Stability',
      question: fr
        ? `Comment ${n} gère-t-il·elle les hauts et les bas de la vie ?`
        : `How does ${n} handle life's ups and downs?`,
      leftLabel:  fr ? `${n} est…`                  : `${n} is…`,
      rightLabel: fr ? 'Iel fonctionne mieux avec…'  : 'They work best with…',
      statements: {
        left: fr ? [
          "Très réactif·ve, les émotions prennent souvent le dessus",
          "Peut être déstabilisé·e par les difficultés",
          "Généralement stable, avec quelques moments difficiles",
          "Calme et fiable sous pression",
          "Inébranlable — la personne la plus stable que je connaisse",
        ] : [
          "Very reactive, emotions often take over",
          "Can be destabilized by difficulties",
          "Generally steady, with some rough patches",
          "Calm and reliable under pressure",
          "Rock-solid — the most stable person I know",
        ],
        right: fr ? [
          "Quelqu'un de spontané qui apporte de l'énergie",
          "Quelqu'un qui tolère bien les hauts et les bas",
          "Quelqu'un d'équilibré qui peut s'adapter",
          "Quelqu'un d'également stable et posé·e",
          "Quelqu'un d'aussi ancré·e et fiable",
        ] : [
          "Someone spontaneous who brings energy",
          "Someone who tolerates ups and downs well",
          "Someone balanced who can adapt",
          "Someone equally steady and composed",
          "Someone just as grounded and reliable",
        ],
      },
    },
    {
      key:'boredom', layer:'A',
      label: t(lang,'axis.boredom') || 'Adventure',
      question: fr
        ? `${n} et la routine…`
        : `${n} and routine…`,
      leftLabel:  fr ? `${n} aime…`                 : `${n} loves…`,
      rightLabel: fr ? "Iel s'épanouirait avec…"     : "They'd thrive with…",
      statements: {
        left: fr ? [
          "La stabilité et la routine — le changement le·la déstabilise",
          "Le familier, prudent·e face aux nouveautés",
          "Un équilibre entre stabilité et nouveauté",
          "Les nouvelles expériences et les aventures régulières",
          "Le changement constant — la routine l'ennuie",
        ] : [
          "Stability and routine — change unsettles them",
          "The familiar, cautious about new things",
          "A balance between stability and novelty",
          "New experiences and regular adventures",
          "Constant change — routine bores them",
        ],
        right: fr ? [
          "Quelqu'un de stable qui aime les habitudes",
          "Quelqu'un de paisible qui prend les choses lentement",
          "Quelqu'un d'ouvert, ni trop routinier ni trop imprévisible",
          "Quelqu'un d'aventureux et curieux",
          "Quelqu'un d'aussi épris·e d'aventure et de nouveauté",
        ] : [
          "Someone stable who enjoys habits and routine",
          "Someone peaceful who takes things slowly",
          "Someone open, neither too routine nor too unpredictable",
          "Someone adventurous and curious",
          "Someone equally adventure-seeking and novelty-driven",
        ],
      },
    },
    {
      key:'social', layer:'A',
      label: t(lang,'axis.social') || 'Social energy',
      question: fr
        ? `À une fête ou un événement, ${n}…`
        : `At a party or event, ${n}…`,
      leftLabel:  fr ? `${n} est…`                  : `${n} is…`,
      rightLabel: fr ? "Iel s'associe mieux avec…"   : 'They match best with…',
      statements: {
        left: fr ? [
          "Très introverti·e, préfère la solitude ou les petits groupes",
          "Se ressource seul·e, sélectif·ve en société",
          "À l'aise dans les deux situations, social·e et solitaire",
          "Énergisé·e par les gens, aime être entouré·e",
          "Totalement extraverti·e — l'âme de chaque rassemblement",
        ] : [
          "Very introverted, prefers solitude or small groups",
          "Recharges alone, selective about social time",
          "Comfortable in both social and quiet settings",
          "Energized by people, loves being out",
          "Total extrovert — the life of any gathering",
        ],
        right: fr ? [
          "Quelqu'un de calme qui aime rester à la maison",
          "Quelqu'un de tranquille qui respecte le besoin de solitude",
          "Quelqu'un flexible selon les moments",
          "Quelqu'un d'également sociable et dynamique",
          "Quelqu'un d'aussi extraverti·e et énergique",
        ] : [
          "Someone calm who enjoys staying in",
          "Someone quiet who respects the need for alone time",
          "Someone flexible depending on the moment",
          "Someone equally social and outgoing",
          "Someone just as extroverted and energetic",
        ],
      },
    },
    {
      key:'stakes', layer:'A',
      label: t(lang,'axis.stakes') || 'Ambition',
      question: fr
        ? `En ce qui concerne ses objectifs, ${n}…`
        : `When it comes to goals, ${n}…`,
      leftLabel:  fr ? `${n} est…`                  : `${n} is…`,
      rightLabel: fr ? "Iel a besoin de quelqu'un…"  : 'They need someone…',
      statements: {
        left: fr ? [
          "Très détendu·e, se satisfait de ce qu'il·elle a",
          "A des intérêts mais pousse rarement fort",
          "Poursuit ses objectifs à un rythme confortable",
          "Ambitieux·se, travaille dur vers des buts précis",
          "Très motivé·e, toujours en train de viser plus haut",
        ] : [
          "Very laid-back, content with what they have",
          "Has interests but rarely pushes hard",
          "Pursues goals at a comfortable pace",
          "Ambitious, works hard toward clear objectives",
          "Highly driven, always pushing further",
        ],
        right: fr ? [
          "Qui valorise l'équilibre et le bonheur simple",
          "Qui n'est pas obsédé·e par la performance",
          "Qui a des aspirations modérées et claires",
          "Qui est aussi motivé·e et déterminé·e",
          "Qui est tout aussi ambitieux·se et déterminé·e à réussir",
        ] : [
          "Who values balance and simple happiness",
          "Who isn't obsessed with achievement",
          "Who has moderate and clear aspirations",
          "Who is equally motivated and driven",
          "Who is just as ambitious and determined to succeed",
        ],
      },
    },
  ];
}

// ── API helpers ───────────────────────────────────────────────────────────
async function apiCall(path, method = 'GET', body = null) {
  const base = process.env.REACT_APP_SUPABASE_URL
    ? `${process.env.REACT_APP_SUPABASE_URL}/functions/v1`
    : 'https://api.beelove.me';
  try {
    const { supabase } = await import('../utils/supabase');
    const { data: { session } } = await supabase?.auth?.getSession?.() || { data: {} };
    const token = session?.access_token;
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${base}${path}`, {
      method, headers,
      body: body ? JSON.stringify(body) : undefined,
    });
    return await res.json().catch(() => ({}));
  } catch (e) {
    return { error: e.message };
  }
}

// ── Design tokens ─────────────────────────────────────────────────────────
const G = {
  bg: '#0A0A0A', dark: '#111', card: '#161616',
  border: 'rgba(255,255,255,0.08)', white: '#F2E8D0',
  muted: '#888', gold: '#C9A84C', goldPale: 'rgba(201,168,76,0.12)',
  goldBdr: 'rgba(201,168,76,0.3)', blue: '#7AB8C4',
  green: '#4EC994', serif: "'Cormorant Garamond',Georgia,serif",
  sans: "'Outfit',system-ui,sans-serif",
};

const CG = '#4EC994'; // green — left pass (how friend is)
const CR = '#E07090'; // rose  — right pass (ideal partner)

// ── Pole labels for each axis ─────────────────────────────────────────────
// self = left pass (how they are), fit = right pass (ideal partner)
const AXIS_POLES = {
  repair:         { fr:['Évite le sujet',          'Affronte directement'     ], en:['Avoids the issue',       'Confronts head-on'        ] },
  conflict:       { fr:['Évite le conflit',         'Très affirmatif·ve'       ], en:['Avoids conflict',        'Very assertive'           ] },
  accountability: { fr:['Dévie la responsabilité',  'Assume tout'              ], en:['Deflects blame',         'Owns everything'          ] },
  generosity:     { fr:['Centré·e sur lui-même',    'Extrêmement généreux·se'  ], en:['Self-focused',          'Extremely generous'       ] },
  presence:       { fr:['Souvent distrait·e',       'Totalement présent·e'     ], en:['Often distracted',      'Fully present'            ] },
  narrative:      { fr:['Encore en recherche',      'Exceptionnellement ancré·e'], en:['Still finding self',   'Exceptionally grounded'   ] },
  stability:      { fr:['Très réactif·ve',          'Inébranlable'             ], en:['Very reactive',         'Rock-solid'               ] },
  boredom:        { fr:['Aime la routine',          'Adore le changement'      ], en:['Loves routine',         'Loves change'             ] },
  social:         { fr:['Très introverti·e',        'Totalement extraverti·e'  ], en:['Very introverted',      'Total extrovert'          ] },
  stakes:         { fr:['Très détendu·e',           'Très ambitieux·se'        ], en:['Very laid-back',        'Highly driven'            ] },
};
const AXIS_POLES_FIT = {
  repair:         { fr:['Laisse se tasser',         'Confronte sans attendre'  ], en:['Lets things settle',    'Confronts right away'     ] },
  conflict:       { fr:['Calme, évite la tension',  'Aussi direct·e'           ], en:['Calm, avoids tension',  'Equally assertive'        ] },
  accountability: { fr:['Pardonne facilement',      'Hauts standards'          ], en:['Forgives easily',       'High standards'           ] },
  generosity:     { fr:['Très indépendant·e',       'Aussi généreux·se'        ], en:['Very independent',      'Equally generous'         ] },
  presence:       { fr:['Donne de l\'espace',       'Présent·e 100% du temps'  ], en:['Gives space',          'Present 100% of the time' ] },
  narrative:      { fr:['Aussi en exploration',     'Aussi accompli·e'         ], en:['Also exploring self',   'Equally grounded'         ] },
  stability:      { fr:['Spontané·e, énergique',    'Aussi stable et posé·e'   ], en:['Spontaneous, energetic','Equally steady'           ] },
  boredom:        { fr:['Aime les habitudes',       'Aussi aventurier·ère'     ], en:['Enjoys habits',         'Equally adventurous'      ] },
  social:         { fr:['Aime rester chez soi',     'Aussi extraverti·e'       ], en:['Enjoys staying in',     'Equally outgoing'         ] },
  stakes:         { fr:['Valorise l\'équilibre',    'Aussi ambitieux·se'       ], en:['Values balance',        'Equally ambitious'        ] },
};

// Axes where left/right poles are displayed swapped to prevent "left = bad" bias
const INVERTED_AXES = new Set(['accountability', 'generosity', 'narrative', 'stability']);

// ── Quick signup — concrete benefits, no abstract "Honey" ─────────────────
function QuickSignup({ ratingId, targetId, targetName, networkId, onDone, lang }) {
  const fr = lang === 'fr';
  const [name,    setName]    = useState('');
  const [email,   setEmail]   = useState('');
  const [loading, setLoading] = useState(false);
  const [done,    setDone]    = useState(false);
  const [error,   setError]   = useState('');

  const submit = async () => {
    if (!name.trim() || !email.trim()) return;
    setLoading(true);
    setError('');
    try {
      const { registerUser } = await import('../utils/supabaseAuth');
      const { error: err, user } = await registerUser({
        email: email.trim(),
        password: Array.from(crypto.getRandomValues(new Uint8Array(18)))
          .map(b => 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$'[b % 60])
          .join(''),
        name: name.trim(),
        age: 25,
        gender: 'nonbinary',
        seeking: ['man','woman','nonbinary'],
        // Inherit target's network — user will refine in onboarding if needed
        // 'montreal' is the launch network, used only when target has no network set
        networkId: networkId || 'ca_montreal',
        signupSource: 'friend_rating',
        referrerId: targetId,
      });
      if (err && !err.includes('taken') && !err.includes('registered')) {
        setError(fr ? 'Une erreur est survenue. Réessaie.' : 'Something went wrong. Try again.');
        setLoading(false);
        return;
      }
      if (user?.id || !err) {
        localStorage.setItem('beelove_pending_trial', JSON.stringify({ source: 'friend_rating', ratingId }));
      }
      setDone(true);
      onDone?.();
    } catch {
      setError(fr ? 'Une erreur est survenue. Réessaie.' : 'Something went wrong. Try again.');
    }
    setLoading(false);
  };

  if (done) return (
    <div style={{ textAlign:'center', padding:'16px 0' }}>
      <div style={{ marginBottom:12 }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(201,168,76,0.75)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m2 7 10 7 10-7"/>
        </svg>
      </div>
      <p style={{ color:G.white, fontFamily:G.serif, fontSize:'1.1rem', marginBottom:4 }}>
        {fr ? 'Vérifie tes emails' : 'Check your email'}
      </p>
      <p style={{ color:G.muted, fontSize:12, lineHeight:1.6 }}>
        {fr
          ? 'Confirme ton adresse pour finaliser ton compte Founding Member.'
          : 'Confirm your address to finalise your Founding Member account.'}
      </p>
    </div>
  );

  return (
    <div>
      {/* Concrete benefit list */}
      <div style={{ background:'rgba(78,201,148,0.06)', border:'1px solid rgba(78,201,148,0.18)', borderRadius:8, padding:'12px 14px', marginBottom:14 }}>
        <p style={{ color:CG, fontSize:11, fontWeight:700, margin:'0 0 8px', letterSpacing:'0.04em' }}>
          {fr ? 'Founding Member — accès gratuit à vie :' : 'Founding Member — free for life:'}
        </p>
        {[
          fr ? `Quelqu'un peut faire la même chose pour toi` : `Someone can do the same for you`,
          fr ? 'Ton profil noté apparaît en premier' : 'Your rated profile appears first',
          fr ? `Vois qui t'a liké sans attendre un match` : 'See who liked you without matching first',
          fr ? 'Badge Founding Member à vie, sans carte bancaire' : 'Founding Member badge for life, no credit card',
        ].map((b, i) => (
          <div key={i} style={{ display:'flex', gap:8, alignItems:'flex-start', marginBottom: i < 3 ? 5 : 0 }}>
            <span style={{ color:CG, fontSize:11, flexShrink:0, marginTop:1 }}>✓</span>
            <span style={{ color:G.muted, fontSize:11, lineHeight:1.4 }}>{b}</span>
          </div>
        ))}
      </div>

      <input value={name} onChange={e => setName(e.target.value)}
        placeholder={fr ? 'Ton prénom' : 'Your first name'}
        style={{ width:'100%', padding:'11px 12px', background:'rgba(255,255,255,0.04)', border:`1px solid ${G.border}`, borderRadius:6, color:G.white, fontFamily:G.sans, fontSize:14, outline:'none', marginBottom:8, boxSizing:'border-box' }}/>
      <input type="email" value={email} onChange={e => setEmail(e.target.value)}
        placeholder="your@email.com"
        style={{ width:'100%', padding:'11px 12px', background:'rgba(255,255,255,0.04)', border:`1px solid ${G.border}`, borderRadius:6, color:G.white, fontFamily:G.sans, fontSize:14, outline:'none', marginBottom:8, boxSizing:'border-box' }}/>
      {error && <p style={{ color:'#E07070', fontSize:12, marginBottom:8 }}>{error}</p>}
      <button onClick={submit} disabled={loading || !name.trim() || !email.trim()} style={{
        width:'100%', padding:'12px', background:G.gold, border:'none', borderRadius:6,
        color:'#0A0A0A', fontFamily:G.sans, fontWeight:700, fontSize:14, cursor:'pointer',
        opacity:(loading || !name.trim() || !email.trim()) ? 0.55 : 1,
      }}>
        {loading
          ? (fr ? 'Création du compte…' : 'Creating account…')
          : (fr ? 'Rejoindre BeeLove — gratuit →' : 'Join BeeLove — free →')}
      </button>
      <p style={{ textAlign:'center', fontSize:11, color:G.muted, marginTop:8 }}>
        {fr ? 'Pas de spam · Confirme ton email pour activer' : 'No spam · Confirm email to activate'}
      </p>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────
export function FriendRating({ token: tokenProp, lang: langProp }) {
  const lang = langProp || 'en';
  const fr   = lang === 'fr';

  const [tokenId,    setTokenId]    = useState(null);
  const [profile,    setProfile]    = useState(null);
  const [tokenErr,   setTokenErr]   = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [ratingCount,  setRatingCount]  = useState(null);
  const [isLoggedIn,   setIsLoggedIn]   = useState(false);
  const [alreadyRated, setAlreadyRated] = useState(false);
  const [previewQuote, setPreviewQuote] = useState(null);
  const [quoteEnabled, setQuoteEnabled] = useState(false);

  // Profile + rating count loading
  useEffect(() => {
    if (!tokenProp) { setTokenErr('No link token provided.'); setLoading(false); return; }
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(tokenProp);
    if (isUUID) {
      import('../utils/supabase').then(async ({ supabase }) => {
        if (!supabase) { setTokenErr('Service unavailable.'); setLoading(false); return; }
        const { data, error } = await supabase
          .from('user_core').select('id, name, age, gender, user_profiles(photos, job, city, bio, network_id)')
          .eq('id', tokenProp).single();
        if (error || !data) { setTokenErr('Profile not found.'); setLoading(false); return; }
        const prof = data.user_profiles?.[0] || {};
        // network_id lives on user_profiles, not user_core — spread covers it
        setProfile({ id: data.id, name: data.name, age: data.age, ...prof });
        supabase.from('friend_ratings').select('id', { count: 'exact', head: true }).eq('target_id', data.id).then(({ count }) => {
          if (count > 0) setRatingCount(count);
        });

        // Prevent same browser from submitting twice for the same target
        const doneKey = `beelove_rated_${data.id}`;
        if (localStorage.getItem(doneKey)) setAlreadyRated(true);

        // Resume in-progress rating (e.g. after auth redirect)
        const resumeRaw = sessionStorage.getItem('beelove_pending_rating_resume');
        if (resumeRaw) {
          try {
            const resume = JSON.parse(resumeRaw);
            if (resume.targetId === data.id) {
              sessionStorage.removeItem('beelove_pending_rating_resume');
              setLeftR(resume.leftR || {});
              setRightR(resume.rightR || {});
              setStep(1);
              setPass('left');
            }
          } catch {}
        }

        setLoading(false);
      });
    } else {
      apiCall(`/friend-rating/token/${tokenProp}`, 'GET').then(data => {
        if (data.error) setTokenErr(data.error);
        else { setTokenId(data.tokenId); setProfile(data.profile); }
        setLoading(false);
      });
    }
  }, [tokenProp]);

  // Auth state check
  useEffect(() => {
    import('../utils/supabase').then(({ supabase }) => {
      supabase?.auth?.getSession?.().then(({ data }) => {
        setIsLoggedIn(!!data?.session?.user);
      });
    });
  }, []);

  // Feature C — friend quote preview (gated by app_feature flag)
  useEffect(() => {
    if (!profile?.id) return;
    import('../utils/supabase').then(async ({ supabase }) => {
      if (!supabase) return;
      const { data: feat } = await supabase
        .from('app_features').select('enabled')
        .eq('key', 'feature_friend_quote_preview').maybeSingle();
      if (!feat?.enabled) return;
      setQuoteEnabled(true);
      const { fetchFriendQuotePreview } = await import('../utils/featurePackA');
      const q = await fetchFriendQuotePreview(profile.id);
      if (q) setPreviewQuote(q);
    });
  }, [profile?.id]);

  // ── Questionnaire state ───────────────────────────────────────────────
  // pass: 'left' | 'trans' | 'right'
  const [step,       setStep]      = useState(0);
  const [pass,       setPass]      = useState('left');
  const [leftR,           setLeftR]           = useState({});
  const [rightR,          setRightR]          = useState({});
  const [selectedPhrases, setSelectedPhrases] = useState([]);
  const [raterName,       setRaterName]       = useState('');
  const [submitting, setSubmitting]= useState(false);
  const [ratingId,   setRatingId]  = useState(null);
  const [signedUp,   setSignedUp]  = useState(false);
  const [linkCopied, setLinkCopied]= useState(false);

  if (loading) return (
    <div style={{ minHeight:'100vh', background:G.bg, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <p style={{ color:G.muted, fontFamily:G.sans, fontSize:13 }}>{fr ? 'Chargement…' : 'Loading…'}</p>
    </div>
  );
  if (tokenErr || !profile) return (
    <div style={{ minHeight:'100vh', background:G.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div style={{ textAlign:'center', maxWidth:320 }}>
        <p style={{ fontFamily:G.serif, fontSize:22, color:G.white, marginBottom:8 }}>
          {fr ? 'Lien indisponible' : 'Link not available'}
        </p>
        <p style={{ color:G.muted, fontSize:13, marginBottom:20, lineHeight:1.6 }}>
          {tokenErr === 'This link has already been used'
            ? (fr ? 'Ce lien a déjà été utilisé.' : 'This link has already been used.')
            : tokenErr === 'This link has expired'
            ? (fr ? 'Ce lien a expiré (30 jours).' : 'This link expired (30 days).')
            : (fr ? 'Ce lien est invalide ou inactif.' : 'This link is invalid or no longer active.')}
        </p>
        <a href="/" style={{ color:G.gold, fontSize:13, fontFamily:G.sans }}>← BeeLove</a>
      </div>
    </div>
  );

  const firstName = (profile.name || 'eux').split(' ')[0];
  const photo     = profile.photos?.[0] || null;
  const AXES      = getAxes(lang, firstName);

  // All 10 axes shown in one table per pass
  const answeredLeft  = AXES.filter(a => (leftR[a.key]  || 0) > 0).length;
  const answeredRight = AXES.filter(a => (rightR[a.key] || 0) > 0).length;

  // Progress bar percentage
  const progressPct = (() => {
    if (step === 0) return 2;
    if (step === 2) return 90;
    if (step === 3) return 100;
    if (pass === 'left')  return  5 + (answeredLeft  / 10) * 40;
    if (pass === 'trans') return 48;
    if (pass === 'right') return 50 + (answeredRight / 10) * 40;
    return 5;
  })();

  // Submit all collected ratings via RPC
  // phraseOverride: pass [] from Skip button to submit with no portrait phrases
  const submitRatings = async (phraseOverride) => {
    const phrases = Array.isArray(phraseOverride) ? phraseOverride : selectedPhrases;
    const langPhrases = PORTRAIT_PHRASES[lang] || PORTRAIT_PHRASES.en;
    const finalQuote = phrases.map(i => langPhrases[i]).join(' ');
    setSubmitting(true);
    let rId = null;
    if (tokenId) {
      const res = await apiCall('/friend-rating/submit', 'POST', {
        tokenId, ratings: leftR, ratingsFit: rightR,
        quote: finalQuote.trim() || null, raterName: raterName || 'Anonymous',
      });
      setSubmitting(false);
      if (res.error) { alert(res.error); return; }
      rId = res.ratingId;
    } else {
      try {
        const { supabase } = await import('../utils/supabase');
        if (!supabase || !profile?.id) throw new Error('Service unavailable');
        const { data, error: rErr } = await supabase.rpc('submit_friend_rating', {
          p_target_id:  profile.id,
          p_rater_name: raterName || 'Anonymous',
          p_quote:      finalQuote.trim() || null,
          p_weight:     1.0,
          p_axes_self:  leftR,
          p_axes_need:  rightR,
        });
        if (rErr) throw new Error(rErr.message);
        if (data?.error) throw new Error(data.error);
        rId = data?.rating_id || null;
      } catch (e) {
        setSubmitting(false);
        alert(e.message || 'Submission failed.');
        return;
      }
      setSubmitting(false);
    }
    setRatingId(rId);
    if (profile?.id) localStorage.setItem(`beelove_rated_${profile.id}`, '1');
    setStep(3);
  };

  const Bar  = () => (
    <div style={{ height:3, background:'rgba(255,255,255,0.05)' }}>
      <div style={{ height:'100%', background:G.gold, width:`${progressPct}%`, transition:'width 0.4s ease' }}/>
    </div>
  );
  const Logo = () => (
    <div style={{ textAlign:'center', padding:'12px 0 8px' }}>
      <span style={{ fontFamily:G.serif, fontSize:17, color:G.gold, fontStyle:'italic' }}>Bee</span>
      <span style={{ fontFamily:G.serif, fontSize:17, color:G.white }}>Love.me</span>
    </div>
  );
  const wrap = (children) => (
    <div style={{ minHeight:'100vh', background:G.bg, fontFamily:G.sans }}>
      <Bar/>
      <div style={{ maxWidth:480, margin:'0 auto', padding:'0 14px 64px' }}>
        <Logo/>
        {children}
      </div>
    </div>
  );

  // ── Step 0 — Intro ────────────────────────────────────────────────────────
  if (step === 0) return wrap(
    <>
      <div style={{ borderRadius:12, overflow:'hidden', marginBottom:14, position:'relative' }}>
        {photo
          ? <img src={photo} alt={firstName} style={{ width:'100%', height:200, objectFit:'cover', objectPosition:'top', display:'block' }}/>
          : <div style={{ width:'100%', height:150, background:G.card, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ fontFamily:G.serif, fontSize:64, color:G.gold, opacity:0.25 }}>{firstName[0]}</span>
            </div>
        }
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(transparent 40%, rgba(10,10,10,0.97) 100%)' }}/>
        <div style={{ position:'absolute', bottom:0, left:0, right:0, padding:12 }}>
          <p style={{ fontFamily:G.serif, fontSize:21, fontWeight:400, color:G.white, margin:'0 0 2px' }}>
            {profile.name}{profile.age ? `, ${profile.age}` : ''}
          </p>
          <p style={{ fontSize:11, color:G.muted, margin:0 }}>{[profile.job, profile.city].filter(Boolean).join(' · ')}</p>
        </div>
      </div>

      {ratingCount > 0 && (
        <p style={{ fontSize:12, color:CG, margin:'0 0 10px', textAlign:'center' }}>
          <strong>{ratingCount}</strong> {ratingCount === 1
            ? (fr ? 'personne a déjà répondu' : 'person already answered')
            : (fr ? 'personnes ont déjà répondu' : 'people already answered')}
        </p>
      )}

      {/* Feature C — anonymized friend quote preview */}
      {quoteEnabled && previewQuote && (
        <div style={{ background:G.card, border:`1px solid ${G.gold}33`, borderRadius:10, padding:'12px 14px', marginBottom:12 }}>
          <p style={{ fontSize:10, letterSpacing:'0.12em', textTransform:'uppercase', color:G.gold, margin:'0 0 6px' }}>
            {fr ? "Un·e ami·e a écrit" : 'A friend wrote'}
          </p>
          <p style={{ fontFamily:G.serif, fontSize:15, color:G.white, margin:0, lineHeight:1.5, fontStyle:'italic' }}>
            "{previewQuote}"
          </p>
        </div>
      )}

      {alreadyRated && (
        <div style={{ display:'flex', gap:8, alignItems:'center', padding:'9px 12px', background:'rgba(201,168,76,0.08)', border:`1px solid rgba(201,168,76,0.25)`, borderRadius:8, marginBottom:12 }}>
          <span style={{ fontSize:14, flexShrink:0 }}>✓</span>
          <p style={{ fontSize:11, color:G.gold, margin:0, lineHeight:1.5 }}>
            {fr ? 'Tu as déjà répondu pour cette personne.' : 'You already answered for this person.'}
          </p>
        </div>
      )}

      <button onClick={() => setStep(1)} style={{
        width:'100%', padding:'14px', background:G.gold, border:'none', borderRadius:8,
        color:'#0A0A0A', fontFamily:G.sans, fontWeight:700, fontSize:16, cursor:'pointer',
        marginBottom:10,
      }}>
        {fr ? `Répondre pour ${firstName} →` : `Rate ${firstName} →`}
      </button>
      <p style={{ textAlign:'center', fontSize:11, color:'rgba(255,255,255,0.28)', marginTop:0, lineHeight:1.8 }}>
        {fr ? `Anonyme · ~3 min · Sans compte` : `Anonymous · ~3 min · No account needed`}
      </p>
    </>
  );

  // ── Step 1 — Two-pass questions ───────────────────────────────────────────
  if (step === 1) {

    // ── Transition between left and right passes ──────────────────────────
    if (pass === 'trans') return wrap(
      <div style={{ textAlign:'center', paddingTop:24 }}>
        <div style={{ fontFamily:G.serif, fontSize:52, color:G.gold, lineHeight:1, marginBottom:16 }}>↔</div>
        <h2 style={{ fontFamily:G.serif, fontSize:22, fontWeight:400, color:G.white, marginBottom:12, lineHeight:1.3 }}>
          {fr ? `Maintenant, de quoi ${firstName} a besoin ?` : `Now, what does ${firstName} need?`}
        </h2>
        <p style={{ fontSize:13, color:G.muted, lineHeight:1.7, marginBottom:28, maxWidth:300, margin:'0 auto 28px' }}>
          {fr
            ? `Mêmes 10 thèmes — maintenant du point de vue du partenaire idéal de ${firstName}.`
            : `Same 10 themes — now from the angle of ${firstName}'s ideal partner.`}
        </p>
        <button onClick={() => setPass('right')} style={{
          width:'100%', padding:'13px', background:G.gold, border:'none', borderRadius:8,
          color:'#0A0A0A', fontFamily:G.sans, fontWeight:700, fontSize:14, cursor:'pointer',
        }}>
          {fr ? 'Continuer →' : 'Continue →'}
        </button>
      </div>
    );

    // ── Left pass — all 10 axes, 7-column semantic differential table ───────
    if (pass === 'left') {
      return wrap(
        <>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
            <span style={{ fontSize:10, fontWeight:600, letterSpacing:'0.12em', textTransform:'uppercase', color:G.gold }}>
              {fr ? 'Comment est' : 'About'} {firstName}
            </span>
            <span style={{ fontSize:12, color:G.muted }}>{answeredLeft} / 10</span>
          </div>

          {/* Column number guides */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr repeat(7, 22px) 1fr', gap:'0 4px', marginBottom:6, alignItems:'center' }}>
            <span/>
            {[1,2,3,4,5,6,7].map(v => (
              <span key={v} style={{ textAlign:'center', fontSize:9, color:'rgba(255,255,255,0.18)', fontFamily:G.sans }}>{v}</span>
            ))}
            <span/>
          </div>

          {AXES.map((ax, rowIdx) => {
            const inv     = INVERTED_AXES.has(ax.key);
            const poles   = AXIS_POLES[ax.key]?.[fr ? 'fr' : 'en'] || ['–', '–'];
            const dispL   = inv ? poles[1] : poles[0];
            const dispR   = inv ? poles[0] : poles[1];
            const stored  = leftR[ax.key] || 0;
            const dispSel = stored > 0 ? (inv ? 8 - stored : stored) : 0;
            const isLayerA = ax.layer === 'A';
            return (
              <>
                {/* Divider between Layer B and Layer A */}
                {isLayerA && rowIdx === 5 && (
                  <div key="divider" style={{ gridColumn:'1/-1', borderTop:'1px solid rgba(255,255,255,0.07)', margin:'6px 0 12px' }}/>
                )}
                <div key={ax.key} style={{
                  display:'grid', gridTemplateColumns:'1fr repeat(7, 22px) 1fr', gap:'0 4px',
                  marginBottom: rowIdx < AXES.length - 1 ? 14 : 6, alignItems:'center',
                }}>
                  <span style={{
                    fontSize:10, lineHeight:1.35, textAlign:'right', paddingRight:6,
                    color: stored > 0 ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.28)',
                    transition:'color 0.2s',
                  }}>{dispL}</span>
                  {[1,2,3,4,5,6,7].map(v => {
                    const sel = dispSel === v;
                    return (
                      <button key={v} onClick={() => setLeftR(r => ({ ...r, [ax.key]: inv ? 8 - v : v }))} style={{
                        width:22, height:22, borderRadius:'50%', padding:0, flexShrink:0,
                        background: sel ? CG : 'transparent',
                        border:`2px solid ${sel ? CG : 'rgba(255,255,255,0.15)'}`,
                        cursor:'pointer', outline:'none',
                        boxShadow: sel ? `0 0 8px ${CG}44` : 'none',
                        transition:'all 0.15s',
                      }}/>
                    );
                  })}
                  <span style={{
                    fontSize:10, lineHeight:1.35, paddingLeft:6,
                    color: stored > 0 ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.28)',
                    transition:'color 0.2s',
                  }}>{dispR}</span>
                </div>
              </>
            );
          })}

          <button
            onClick={() => setPass('trans')}
            disabled={answeredLeft < 10}
            style={{
              width:'100%', padding:'12px', borderRadius:8, border:'none', marginTop:10,
              background: answeredLeft >= 10 ? CG : 'rgba(255,255,255,0.05)',
              color: answeredLeft >= 10 ? '#0A0A0A' : G.muted,
              fontFamily:G.sans, fontWeight:700, fontSize:14,
              cursor: answeredLeft >= 10 ? 'pointer' : 'not-allowed',
              transition:'all 0.2s',
            }}
          >
            {answeredLeft < 10
              ? (fr ? `Réponds à toutes les lignes (${answeredLeft}/10)` : `Answer all rows (${answeredLeft}/10)`)
              : (fr ? 'Continuer →' : 'Continue →')}
          </button>
        </>
      );
    }

    // ── Right pass — "L'être idéal pour [Prénom]" — same 10-row table ────────
    if (pass === 'right') {
      return wrap(
        <>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
            <span style={{ fontSize:10, fontWeight:600, letterSpacing:'0.12em', textTransform:'uppercase', color:'rgba(224,112,144,0.85)' }}>
              {fr ? `L'être idéal pour` : `Ideal match for`} {firstName}
            </span>
            <span style={{ fontSize:12, color:G.muted }}>{answeredRight} / 10</span>
          </div>

          {/* Column number guides */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr repeat(7, 22px) 1fr', gap:'0 4px', marginBottom:6, alignItems:'center' }}>
            <span/>
            {[1,2,3,4,5,6,7].map(v => (
              <span key={v} style={{ textAlign:'center', fontSize:9, color:'rgba(255,255,255,0.18)', fontFamily:G.sans }}>{v}</span>
            ))}
            <span/>
          </div>

          {AXES.map((ax, rowIdx) => {
            const inv     = INVERTED_AXES.has(ax.key);
            const poles   = AXIS_POLES_FIT[ax.key]?.[fr ? 'fr' : 'en'] || ['–', '–'];
            const dispL   = inv ? poles[1] : poles[0];
            const dispR   = inv ? poles[0] : poles[1];
            const stored  = rightR[ax.key] || 0;
            const dispSel = stored > 0 ? (inv ? 8 - stored : stored) : 0;
            const isLayerA = ax.layer === 'A';
            return (
              <>
                {isLayerA && rowIdx === 5 && (
                  <div key="divider" style={{ gridColumn:'1/-1', borderTop:'1px solid rgba(255,255,255,0.07)', margin:'6px 0 12px' }}/>
                )}
                <div key={ax.key} style={{
                  display:'grid', gridTemplateColumns:'1fr repeat(7, 22px) 1fr', gap:'0 4px',
                  marginBottom: rowIdx < AXES.length - 1 ? 14 : 6, alignItems:'center',
                }}>
                  <span style={{
                    fontSize:10, lineHeight:1.35, textAlign:'right', paddingRight:6,
                    color: stored > 0 ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.28)',
                    transition:'color 0.2s',
                  }}>{dispL}</span>
                  {[1,2,3,4,5,6,7].map(v => {
                    const sel = dispSel === v;
                    return (
                      <button key={v} onClick={() => setRightR(r => ({ ...r, [ax.key]: inv ? 8 - v : v }))} style={{
                        width:22, height:22, borderRadius:'50%', padding:0, flexShrink:0,
                        background: sel ? CR : 'transparent',
                        border:`2px solid ${sel ? CR : 'rgba(255,255,255,0.15)'}`,
                        cursor:'pointer', outline:'none',
                        boxShadow: sel ? `0 0 8px ${CR}44` : 'none',
                        transition:'all 0.15s',
                      }}/>
                    );
                  })}
                  <span style={{
                    fontSize:10, lineHeight:1.35, paddingLeft:6,
                    color: stored > 0 ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.28)',
                    transition:'color 0.2s',
                  }}>{dispR}</span>
                </div>
              </>
            );
          })}

          <button
            onClick={() => setStep(2)}
            disabled={answeredRight < 10}
            style={{
              width:'100%', padding:'12px', borderRadius:8, border:'none', marginTop:10,
              background: answeredRight >= 10 ? CR : 'rgba(255,255,255,0.05)',
              color: answeredRight >= 10 ? '#0A0A0A' : G.muted,
              fontFamily:G.sans, fontWeight:700, fontSize:14,
              cursor: answeredRight >= 10 ? 'pointer' : 'not-allowed',
              transition:'all 0.2s',
            }}
          >
            {answeredRight < 10
              ? (fr ? `Réponds à toutes les lignes (${answeredRight}/10)` : `Answer all rows (${answeredRight}/10)`)
              : (fr ? 'Terminer →' : 'Finish →')}
          </button>
        </>
      );
    }
  }

  // ── Step 2 — Portrait phrase picker + submit ─────────────────────────────
  if (step === 2) {
    const langPhrases = PORTRAIT_PHRASES[lang] || PORTRAIT_PHRASES.en;
    const maxPhrases  = 2;
    const togglePhrase = (i) => {
      setSelectedPhrases(prev => {
        if (prev.includes(i)) return prev.filter(x => x !== i);
        if (prev.length >= maxPhrases) return prev;
        return [...prev, i];
      });
    };

    return wrap(
      <>
        <input
          value={raterName} onChange={e => setRaterName(e.target.value)}
          placeholder={fr ? 'Ton prénom (optionnel)' : 'Your name (optional)'}
          style={{ width:'100%', padding:'10px 14px', background:'rgba(255,255,255,0.04)', border:`1px solid ${G.border}`, borderRadius:8, color:G.white, fontFamily:G.sans, fontSize:14, outline:'none', marginBottom:14, boxSizing:'border-box' }}
        />

        <div style={{ background:G.card, border:`1px solid ${G.border}`, borderRadius:10, padding:16, marginBottom:14 }}>
          <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:4 }}>
            <h3 style={{ fontFamily:G.serif, fontSize:18, fontWeight:400, color:G.white, margin:0 }}>
              {fr ? 'Un portrait en une phrase ?' : 'A one-sentence portrait?'}
            </h3>
            <span style={{ color:G.muted, fontSize:12, flexShrink:0, marginLeft:8 }}>{fr ? 'Optionnel' : 'Optional'}</span>
          </div>
          <p style={{ fontSize:12, color:G.muted, lineHeight:1.6, marginBottom:12 }}>
            {fr
              ? `Choisis 1 ou 2 phrases qui correspondent à ${firstName}. Elles apparaîtront anonymement sur son profil.`
              : `Pick 1 or 2 phrases that fit ${firstName}. They'll appear anonymously on their profile.`}
          </p>

          <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
            {langPhrases.map((phrase, i) => {
              const sel       = selectedPhrases.includes(i);
              const maxed     = !sel && selectedPhrases.length >= maxPhrases;
              return (
                <button key={i} onClick={() => !maxed && togglePhrase(i)} style={{
                  width:'100%', padding:'10px 14px', textAlign:'left',
                  background: sel ? G.goldPale : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${sel ? G.goldBdr : 'rgba(255,255,255,0.06)'}`,
                  borderRadius:8, cursor: maxed ? 'default' : 'pointer',
                  display:'flex', alignItems:'center', gap:10,
                  opacity: maxed ? 0.38 : 1,
                  transition:'background 0.13s, border-color 0.13s, opacity 0.13s',
                }}>
                  <div style={{
                    width:8, height:8, borderRadius:'50%', flexShrink:0,
                    background: sel ? G.gold : 'rgba(255,255,255,0.12)',
                    transition:'background 0.13s',
                  }}/>
                  <span style={{ fontSize:12, lineHeight:1.5, color: sel ? G.gold : G.muted }}>
                    {phrase}
                  </span>
                </button>
              );
            })}
          </div>

          {selectedPhrases.length >= maxPhrases && (
            <p style={{ fontSize:11, color:G.gold, marginTop:10, textAlign:'center', opacity:0.8 }}>
              {fr ? `Maximum ${maxPhrases} phrases sélectionnées.` : `Maximum ${maxPhrases} phrases selected.`}
            </p>
          )}
        </div>

        <button onClick={submitRatings} disabled={submitting} style={{
          width:'100%', padding:'13px', background:G.gold, border:'none', borderRadius:8,
          color:'#0A0A0A', fontFamily:G.sans, fontWeight:700, fontSize:14, cursor:'pointer',
          marginBottom:8, opacity:submitting ? 0.6 : 1,
        }}>
          {submitting ? (fr ? 'Envoi…' : 'Submitting…') : (fr ? 'Envoyer mon avis ✦' : 'Submit my vote ✦')}
        </button>
        <button onClick={() => submitRatings([])} disabled={submitting} style={{
          width:'100%', padding:'10px', background:'transparent', border:`1px solid ${G.border}`,
          borderRadius:8, color:G.muted, fontFamily:G.sans, fontSize:13, cursor:'pointer',
        }}>
          {fr ? 'Passer — envoyer sans phrase' : 'Skip — submit without a phrase'}
        </button>
      </>
    );
  }

  // ── Step 3 — Done + conversion CTA ───────────────────────────────────────
  return wrap(
    <>
      <div style={{ textAlign:'center', marginBottom:22 }}>
        <h2 style={{ fontFamily:G.serif, fontSize:21, fontWeight:400, color:G.white, margin:'16px 0 6px', lineHeight:1.3 }}>
          {fr ? 'Merci pour ton honnêteté.' : 'Thank you for being honest.'}
        </h2>
        <p style={{ fontSize:13, color:G.muted, lineHeight:1.7, maxWidth:300, margin:'0 auto' }}>
          {fr
            ? <>Ton avis aide <strong style={{ color:G.white }}>{firstName}</strong> à trouver quelqu'un qui le·la mérite vraiment.</>
            : <>Your vote helps <strong style={{ color:G.white }}>{firstName}</strong> find someone truly worthy of them.</>}
        </p>
      </div>

      {!signedUp ? (
        <div style={{ background:G.card, border:`1px solid ${G.border}`, borderRadius:12, padding:18, marginBottom:14 }}>
          <p style={{ fontFamily:G.serif, fontSize:17, fontWeight:400, color:G.white, marginBottom:4 }}>
            {fr ? `Tu viens d'aider ${firstName}.` : `You just helped ${firstName}.`}
          </p>
          <p style={{ fontSize:11, color:'rgba(78,201,148,0.85)', marginBottom:14 }}>
            ✓ {fr ? 'Tes réponses sont déjà enregistrées.' : 'Your answers are already saved.'}
          </p>
          <QuickSignup
            ratingId={ratingId}
            targetId={profile.id}
            targetName={firstName}
            networkId={profile.network_id}
            lang={lang}
            onDone={() => setSignedUp(true)}
          />
        </div>
      ) : (
        <div style={{ background:G.goldPale, border:`1px solid ${G.goldBdr}`, borderRadius:12, padding:18, textAlign:'center', marginBottom:14 }}>
          <p style={{ fontFamily:G.serif, fontSize:18, color:G.gold, marginBottom:6 }}>
            {fr ? 'Bienvenue ✦' : "You're in ✦"}
          </p>
          <p style={{ fontSize:13, color:G.muted, lineHeight:1.6 }}>
            {fr
              ? 'Confirme ton e-mail pour finaliser ton compte Founding Member.'
              : 'Confirm your email to finalise your Founding Member account.'}
          </p>
        </div>
      )}

      {/* Cascade sharing */}
      {profile?.id && (
        <div style={{ background:'rgba(255,255,255,0.02)', border:`1px solid ${G.border}`, borderRadius:12, padding:16, marginBottom:14 }}>
          <p style={{ fontSize:13, color:G.white, fontWeight:600, margin:'0 0 4px', fontFamily:G.serif }}>
            {fr ? `Tu connais quelqu'un d'autre qui pourrait aider ${firstName} ?` : `Know another friend who could help ${firstName}?`}
          </p>
          <p style={{ fontSize:11, color:G.muted, margin:'0 0 12px', lineHeight:1.5 }}>
            {fr ? 'Les réponses s\'accumulent — plus il y en a, meilleur le profil.' : 'Answers add up — the more, the better the profile.'}
          </p>
          <div style={{ display:'flex', gap:8 }}>
            {typeof navigator !== 'undefined' && navigator.share ? (
              <button onClick={() => navigator.share({ title:`Help ${firstName} find love`, url:`${window.location.origin}/#rate/${profile.id}` })} style={{
                flex:1, padding:'10px', background:G.gold, border:'none', borderRadius:6,
                color:'#0A0A0A', fontFamily:G.sans, fontWeight:700, fontSize:13, cursor:'pointer',
              }}>
                {fr ? 'Partager →' : 'Share →'}
              </button>
            ) : null}
            <button onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/#rate/${profile.id}`).then(() => {
                setLinkCopied(true);
                setTimeout(() => setLinkCopied(false), 2500);
              });
            }} style={{
              flex:1, padding:'10px', background:'transparent', border:`1px solid ${G.border}`,
              borderRadius:6, color: linkCopied ? CG : G.muted, fontFamily:G.sans, fontSize:13, cursor:'pointer', transition:'color 0.2s',
            }}>
              {linkCopied ? (fr ? '✓ Copié !' : '✓ Copied!') : (fr ? 'Copier le lien' : 'Copy link')}
            </button>
          </div>
        </div>
      )}

      <a href="/" style={{ display:'block', textAlign:'center', fontSize:12, color:G.muted, textDecoration:'none', paddingTop:4 }}>
        ← BeeLove
      </a>
    </>
  );
}

export default FriendRating;
